import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Async thunk to create a checkout session
export const createCheckout = createAsyncThunk(
  "checkout/createCheckout",
  async (checkoutData, { rejectWithValue }) => {
    try {
      // ✅ Validate required data before sending
      if (!checkoutData.checkoutItems || checkoutData.checkoutItems.length === 0) {
        throw new Error("No items to checkout");
      }

      if (!checkoutData.shippingAddress) {
        throw new Error("Shipping address is required");
      }

      // ✅ Updated field validation to match form fields
      // firstName / lastName are validated in Checkout.jsx (required for "A Friend" only;
      // for "Myself" they come from the profile and may be a single word / empty)
      const requiredFields = ['address', 'city', 'state', 'postalCode', 'country', 'phone'];
      const missingFields = requiredFields.filter(field =>
        !checkoutData.shippingAddress[field] || !checkoutData.shippingAddress[field].trim()
      );

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // ✅ Validate phone number format
      if (!/^[\+]?[1-9][\d]{9,15}$/.test(checkoutData.shippingAddress.phone.replace(/[\s\-\(\)]/g, ''))) {
        throw new Error("Please enter a valid phone number");
      }

      console.log('Sending checkout data:', checkoutData);

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/checkout`,
        checkoutData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Checkout creation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Checkout creation error:', error);
      
      if (error.response) {
        return rejectWithValue({
          message: error.response.data?.message || 'Server error occurred',
          errors: error.response.data?.errors || [],
          status: error.response.status
        });
      } else if (error.request) {
        return rejectWithValue({
          message: 'Network error - please check your connection',
          errors: []
        });
      } else {
        return rejectWithValue({
          message: error.message || 'An unexpected error occurred',
          errors: []
        });
      }
    }
  }
);

// Rest of the slice remains the same...
export const getCheckout = createAsyncThunk(
  "checkout/getCheckout",
  async (checkoutId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/checkout/${checkoutId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          }
        }
      );


      //and this
      return {
  success: response.data.success,
  _id: response.data._id || response.data.checkout?._id,
  checkout: response.data.checkout
};





      
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to fetch checkout details',
        status: error.response?.status
      });
    }
  }
);

export const updatePaymentStatus = createAsyncThunk(
  "checkout/updatePaymentStatus",
  async ({ checkoutId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/checkout/${checkoutId}/pay`,
        paymentData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to update payment status',
        status: error.response?.status
      });
    }
  }
);

export const finalizeCheckout = createAsyncThunk(
  "checkout/finalizeCheckout",
  async (checkoutId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/checkout/${checkoutId}/finalize`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to finalize checkout',
        status: error.response?.status
      });
    }
  }
);

const checkoutSlice = createSlice({
  name: "checkout",
  initialState: {
    checkout: null,
    order: null,
    loading: false,
    error: null,
    paymentLoading: false,
    finalizationLoading: false,
  },
  reducers: {
    clearCheckout: (state) => {
      state.checkout = null;
      state.order = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create Checkout
      .addCase(createCheckout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCheckout.fulfilled, (state, action) => {
        state.loading = false;
        state.checkout = action.payload;
        state.error = null;
      })
      .addCase(createCheckout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'Failed to create checkout';
      })
      // Get Checkout
      .addCase(getCheckout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCheckout.fulfilled, (state, action) => {
        state.loading = false;
        state.checkout = action.payload;
        state.error = null;
      })
      .addCase(getCheckout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch checkout';
      })
      // Update Payment Status
      .addCase(updatePaymentStatus.pending, (state) => {
        state.paymentLoading = true;
        state.error = null;
      })
      .addCase(updatePaymentStatus.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.checkout = action.payload;
        state.error = null;
      })
      .addCase(updatePaymentStatus.rejected, (state, action) => {
        state.paymentLoading = false;
        state.error = action.payload?.message || 'Failed to update payment status';
      })
      // Finalize Checkout
      .addCase(finalizeCheckout.pending, (state) => {
        state.finalizationLoading = true;
        state.error = null;
      })
      .addCase(finalizeCheckout.fulfilled, (state, action) => {
        state.finalizationLoading = false;
        state.order = action.payload;
        state.error = null;
      })
      .addCase(finalizeCheckout.rejected, (state, action) => {
        state.finalizationLoading = false;
        state.error = action.payload?.message || 'Failed to finalize checkout';
      });
  },
});

export const { clearCheckout, clearError } = checkoutSlice.actions;
export default checkoutSlice.reducer;
